import {
  InputValue,
  OrdinaryConfigContext,
  WidgetFormSubmissionWithWidget
} from "@closerplatform/spinner-openapi";
import { addMilliseconds, differenceInMilliseconds } from "date-fns";
import { fromPairs } from "lodash";
import {
  UserAnswers,
  WidgetInput,
  WidgetInputCheckboxList,
  WidgetInputMultipleButtons,
  WidgetInputRadioButtons,
  WidgetInputRadioList,
  WidgetInputText,
  WidgetInputType
} from "../types";

export const createUsersAnswers = (
  { userInputs }: WidgetFormSubmissionWithWidget,
  widgetConfig: OrdinaryConfigContext
): UserAnswers => {
  const emptyMultipleButtonsInputs =
    widgetConfig.multipleButtonsInputs?.map<[string, InputValue]>((i) => [
      i.name,
      { inputName: i.name, values: [] }
    ]) || [];
  const emptyCheckboxListInputs =
    widgetConfig.checkboxListInputs?.map<[string, InputValue]>((i) => [
      i.name,
      { inputName: i.name, values: [] }
    ]) || [];
  const emptyTextInputs = widgetConfig.textInputs?.map<[string, InputValue]>((i) => [
    i.name,
    { inputName: i.name, values: [] }
  ]) || [];

  return userInputs
    ? createUserAnswersFromUserInputs(userInputs)
    : fromPairs([...emptyMultipleButtonsInputs, ...emptyCheckboxListInputs, ...emptyTextInputs]);
};

export const createUserAnswersFromUserInputs = (userInputs: InputValue[]): UserAnswers => {
  return fromPairs(userInputs.map((userInput) => [userInput.inputName, userInput]));
};

export const createWidgetInputsFromConfig = (
  widgetConfig: OrdinaryConfigContext
): WidgetInput[] => {
  const radioButtonsInputs: WidgetInputRadioButtons[] = widgetConfig.radioButtonsInputs
    ? widgetConfig.radioButtonsInputs.map((i) => ({ ...i, type: WidgetInputType.RadioButtons }))
    : [];
  const multipleButtonsInput: WidgetInputMultipleButtons[] = widgetConfig.multipleButtonsInputs
    ? widgetConfig.multipleButtonsInputs.map((i) => ({
        ...i,
        type: WidgetInputType.MultipleButtons
      }))
    : [];
  const radioListInputs: WidgetInputRadioList[] = widgetConfig.radioListInputs
    ? widgetConfig.radioListInputs.map((i) => ({ ...i, type: WidgetInputType.RadioList }))
    : [];
  const checkboxListInputs: WidgetInputCheckboxList[] = widgetConfig.checkboxListInputs
    ? widgetConfig.checkboxListInputs.map((i) => ({ ...i, type: WidgetInputType.CheckboxList }))
    : [];
  const textInputs: WidgetInputText[] = widgetConfig.textInputs
    ? widgetConfig.textInputs.map((i) => ({ ...i, type: WidgetInputType.Text }))
    : [];

  const inputs = [
    ...radioButtonsInputs,
    ...multipleButtonsInput,
    ...radioListInputs,
    ...checkboxListInputs,
    ...textInputs
  ];

  return inputs.sort((a, b) => a.index - b.index);
};

/** Returns milliseconds to expiration (might be negative), or undefined if does not expire */
export const calculateExpiresIn = ({
  createdAt,
  expireInterval
}: WidgetFormSubmissionWithWidget): number | undefined => {
  if (!expireInterval) {
    return;
  }

  const dateCreatedAt = new Date(createdAt);
  const dateNow = Date.now();
  const dateExpiration = addMilliseconds(dateCreatedAt, expireInterval);

  return differenceInMilliseconds(dateExpiration, dateNow);
};
